import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { ErrorDetailsActions } from '../error-details/error-details.actions';
import { ErrorDetailsSelectors } from '../error-details/error-details.selectors';
import { ErrorDetailsState } from '../error-details/error-details.state';
import { GatewaysActions } from '../gateways/gateways.actions';
import { GatewaysSelectors } from '../gateways/gateways.selectors';
import { GatewaysState } from '../gateways/gateways.state';
import { InstallationsSearchActions } from '../installations-search/installations-search.actions';
import { InstallationsSearchSelectors } from '../installations-search/installations-search.selectors';
import { InstallationsSearchState } from '../installations-search/installations-search.state';

import { SearchActions } from './search.actions';
import { SearchListType } from './search.model';
import { SearchSelectors } from './search.selectors';
import { SearchListState } from './search.state';

@Injectable({ providedIn: 'root' })
export class SearchStore {
  state$ = this.store.select(SearchSelectors.getState());

  constructor(private store: Store) {}

  getErrors(): Observable<ErrorDetailsState> {
    return this.store.select(ErrorDetailsSelectors.getState());
  }

  getGateways(): Observable<GatewaysState> {
    return this.store.select(GatewaysSelectors.getState());
  }

  getInstallations(): Observable<InstallationsSearchState> {
    return this.store.select(InstallationsSearchSelectors.getState());
  }

  getSearchHistory(): Observable<SearchListState> {
    return this.store.select(SearchSelectors.getState());
  }

  searchCompanyUsers(companyNumber: string): void {
    this.store.dispatch(
      SearchActions.AddToListSearch({
        payload: {
          type: SearchListType.User,
          searchParams: {
            companyNumber,
          },
          timestamp: new Date().toISOString(),
        },
      })
    );
  }

  searchUsers(
    loginId: string,
    firstName: string,
    familyName: string,
    city: string,
    street: string,
    postalCode: string
  ): void {
    this.store.dispatch(
      SearchActions.AddToListSearch({
        payload: {
          type: SearchListType.User,
          searchParams: {
            loginId,
            firstName,
            familyName,
            city,
            street,
            postalCode,
          },
          timestamp: new Date().toISOString(),
        },
      })
    );
  }

  fetchMoreGateways(searchParams: any, limit: number, cursor: string): void {
    this.store.dispatch(
      GatewaysActions.listGatewaysSearchFetchMore({
        gatewaySerial: searchParams.gatewaySerial,
        limit,
        cursor,
      })
    );
  }

  fetchMoreInstallations(searchParams: any, limit: number, cursor: string): void {
    this.store.dispatch(
      InstallationsSearchActions.listInstallationsSearchFetchMore({
        gatewaySerial: searchParams.gatewaySerial,
        boilerSerial: searchParams.boilerSerial,
        limit,
        cursor,
      })
    );
  }

  searchErrors(
    materialNumber: string,
    errorCode: string,
    countryCode: string,
    languageCode: string
  ): void {
    const shortBoilerSerial = materialNumber.slice(0, 7);

    this.store.dispatch(
      SearchActions.AddToListSearch({
        payload: {
          type: SearchListType.Error,
          searchParams: {
            materialNumber: shortBoilerSerial,
            errorCode,
            countryCode,
            languageCode,
          },
          timestamp: new Date().toISOString(),
        },
      })
    );

    this.store.dispatch(
      ErrorDetailsActions.getErrorDetails.attempt({
        materialNumber: shortBoilerSerial,
        errorCode,
        countryCode,
        languageCode,
      })
    );
  }

  searchInstallations(gatewaySerial: string, boilerSerial: string): void {
    this.store.dispatch(
      SearchActions.AddToListSearch({
        payload: {
          type: SearchListType.Boiler,
          searchParams: {
            gatewaySerial,
            boilerSerial,
          },
          timestamp: new Date().toISOString(),
        },
      })
    );

    this.store.dispatch(
      InstallationsSearchActions.listInstallationsSearchResult({
        gatewaySerial,
        boilerSerial,
      })
    );
  }

  searchGateways(gatewaySerial: string, limit?: number): void {
    this.store.dispatch(
      SearchActions.AddToListSearch({
        payload: {
          type: SearchListType.Gateway,
          searchParams: {
            gatewaySerial,
          },
          timestamp: new Date().toISOString(),
        },
      })
    );

    this.store.dispatch(
      GatewaysActions.listGatewaysSearchResult({
        gatewaySerial,
        limit,
      })
    );
  }
}
